// AR scan mode: the pure parts (2026-10-14, step 1 of
// reports/ar_scan_mode_spec_2026-10-14.md). No DOM, no imports: unit-tested
// in Node (frontend/tests/scanCore.test.ts). scan.ts is the DOM glue.
//
//  * ScanScheduler  when to take the next frame while recording: every
//                   ~1-2.5 s while moving or turning, rarely when standing
//                   still, never faster than min_interval_s;
//  * EstimateRing   a short ring buffer of the position filter's own
//                   estimate(t) (TRACKER_NOTES section 4). Step 1 records the
//                   estimate at each frame's capture time; step 5 uses the
//                   same ring to apply a late scan fix as
//                   anchorShift(now, fix - ring.at(t_capture)) because
//                   PositionFilter has no rewind;
//  * buildFrameMeta the metadata uploaded with each frame;
//  * queueState     the offline cap (stop capturing when too many frames wait);
//  * recLabel       the recording indicator's text.

export interface ScanCadence {
  min_interval_s: number;
  max_interval_s: number;
  stationary_interval_s: number;
  min_move_m: number;
  min_turn_deg: number;
  long_edge_px: number;
  jpeg_quality: number;
  max_queued_frames: number;
}

/** Used until the server's /api/field/scans/config arrives (or offline). Same values as config/field_scans.yaml. */
export const DEFAULT_CADENCE: ScanCadence = {
  min_interval_s: 1.0,
  max_interval_s: 2.5,
  stationary_interval_s: 6.0,
  min_move_m: 0.6,
  min_turn_deg: 12,
  long_edge_px: 1280,
  jpeg_quality: 0.8,
  max_queued_frames: 300,
};

function wrap180(d: number): number {
  return ((((d + 180) % 360) + 360) % 360) - 180;
}

export interface ScanSample {
  /** seconds (performance clock) */
  t: number;
  /** position filter estimate, local metres (null: no estimate yet) */
  e: number | null;
  n: number | null;
  /** camera heading, degrees (null: no compass) */
  heading: number | null;
}

export type CaptureReason = "first" | "moved" | "turned" | "interval" | "stationary";

/** Decides when the next frame is due. Feed it samples; call `taken` when a
 * frame was actually captured. */
export class ScanScheduler {
  private last: ScanSample | null = null;
  cadence: ScanCadence;
  constructor(cadence: ScanCadence = DEFAULT_CADENCE) {
    this.cadence = cadence;
  }

  due(s: ScanSample): CaptureReason | null {
    const c = this.cadence;
    const L = this.last;
    if (!L) return "first";
    const dt = s.t - L.t;
    if (dt < c.min_interval_s) return null;
    const moved = s.e !== null && s.n !== null && L.e !== null && L.n !== null ? Math.hypot(s.e - L.e, s.n - L.n) : 0;
    const turned = s.heading !== null && L.heading !== null ? Math.abs(wrap180(s.heading - L.heading)) : 0;
    if (moved >= c.min_move_m) return "moved";
    if (turned >= c.min_turn_deg) return "turned";
    // moving a little / turning a little: keep a steady stream
    const active = moved >= c.min_move_m * 0.25 || turned >= c.min_turn_deg * 0.25;
    if (active && dt >= c.max_interval_s) return "interval";
    if (dt >= c.stationary_interval_s) return "stationary";
    return null;
  }

  taken(s: ScanSample): void {
    this.last = { ...s };
  }

  reset(): void {
    this.last = null;
  }
}

export interface RingEntry {
  t: number;
  e: number;
  n: number;
  sigma: number;
}

/** The filter's own estimate over the last `spanS` seconds, interpolated. */
export class EstimateRing {
  private buf: RingEntry[] = [];
  spanS: number;
  constructor(spanS = 10) {
    this.spanS = spanS;
  }

  push(x: RingEntry): void {
    if (![x.t, x.e, x.n, x.sigma].every(Number.isFinite)) return;
    const last = this.buf[this.buf.length - 1];
    if (last && x.t < last.t) this.buf = []; // a clock jump / new frame: start again
    if (last && x.t === last.t) this.buf[this.buf.length - 1] = x;
    else this.buf.push(x);
    const cut = x.t - this.spanS;
    while (this.buf.length > 2 && this.buf[1].t <= cut) this.buf.shift();
  }

  /** The estimate at time t (linear between samples; clamped to the ends
   * within `slackS`, else null: too old or in the future). */
  at(t: number, slackS = 0.5): RingEntry | null {
    const b = this.buf;
    if (!b.length) return null;
    if (t < b[0].t - slackS || t > b[b.length - 1].t + slackS) return null;
    if (t <= b[0].t) return { ...b[0], t };
    if (t >= b[b.length - 1].t) return { ...b[b.length - 1], t };
    let lo = 0, hi = b.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (b[mid].t <= t) lo = mid;
      else hi = mid;
    }
    const a = b[lo], z = b[hi];
    const f = z.t === a.t ? 0 : (t - a.t) / (z.t - a.t);
    return { t, e: a.e + f * (z.e - a.e), n: a.n + f * (z.n - a.n), sigma: Math.max(a.sigma, z.sigma) };
  }

  get size(): number {
    return this.buf.length;
  }

  clear(): void {
    this.buf = [];
  }
}

export interface FrameInputs {
  clientUuid: string;
  seq: number;
  /** capture time on the phone clock, epoch ms */
  tMs: number;
  /** capture time on the performance clock, seconds (the ring's clock) */
  tPerf: number;
  reason: CaptureReason;
  gps: { lat: number; lon: number; accuracy: number | null; at?: number | null } | null;
  pose: { heading: number | null; heading_accuracy: number | null; pitch: number | null; roll: number | null;
    source?: string | null; disturbed?: boolean; screen_angle?: number } | null;
  /** the filter's estimate at capture time (lat/lon of the local e/n), or null */
  estimate: { lat: number; lon: number; sigma: number; e: number; n: number; origin_lat: number; origin_lon: number;
    held?: boolean; motion?: string } | null;
  camera: { fov_deg: number; width: number; height: number; video_width: number; video_height: number } | null;
  calibration: { heading_offset_deg: number; east_m: number; north_m: number } | null;
  vo: { on: boolean; quality: string | null } | null;
  /** the session create, so a frame can start a session whose create was lost */
  session: Record<string, unknown> | null;
}

function r(v: number | null | undefined, digits: number): number | null {
  if (v === null || v === undefined || !Number.isFinite(v)) return null;
  const k = 10 ** digits;
  return Math.round(v * k) / k;
}

/** The JSON `meta` sent with a frame (the server validates the numbers it indexes and keeps the rest as sent). */
export function buildFrameMeta(i: FrameInputs): Record<string, unknown> {
  const pose = i.pose
    ? { heading: r(i.pose.heading, 2), heading_accuracy: r(i.pose.heading_accuracy, 1), pitch: r(i.pose.pitch, 2),
        roll: r(i.pose.roll, 2), source: i.pose.source ?? null, disturbed: !!i.pose.disturbed,
        screen_angle: i.pose.screen_angle ?? 0 }
    : null;
  const est = i.estimate
    ? { lat: r(i.estimate.lat, 7), lon: r(i.estimate.lon, 7), sigma: r(i.estimate.sigma, 2), e: r(i.estimate.e, 3),
        n: r(i.estimate.n, 3), origin_lat: r(i.estimate.origin_lat, 7), origin_lon: r(i.estimate.origin_lon, 7),
        held: !!i.estimate.held, motion: i.estimate.motion ?? null, t: r(i.tPerf, 3) }
    : null;
  const out: Record<string, unknown> = {
    client_uuid: i.clientUuid,
    seq: i.seq,
    t_ms: Math.round(i.tMs),
    t_perf_s: r(i.tPerf, 3),
    reason: i.reason,
    gps: i.gps ? { lat: r(i.gps.lat, 7), lon: r(i.gps.lon, 7), accuracy: r(i.gps.accuracy, 1), at: i.gps.at ?? null } : null,
    pose,
    estimate: est,
    camera: i.camera,
    calibration: i.calibration
      ? { heading_offset_deg: r(i.calibration.heading_offset_deg, 2), east_m: r(i.calibration.east_m, 2), north_m: r(i.calibration.north_m, 2) }
      : null,
    vo: i.vo,
  };
  if (i.session) out.session = i.session;
  return out;
}

export type QueueLevel = "ok" | "backlog" | "full";

/** Offline: keep capturing until `max` frames wait; warn from 2/3 of it. */
export function queueState(queued: number, max: number): QueueLevel {
  if (queued >= max) return "full";
  if (queued >= Math.ceil((max * 2) / 3)) return "backlog";
  return "ok";
}

/** The recording indicator: "REC 0:42 · 23 sent · 4 waiting". */
export function recLabel(elapsedS: number, sent: number, queued: number, level: QueueLevel): string {
  const s = Math.max(0, Math.floor(elapsedS));
  const clock = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const parts = [`REC ${clock}`, `${sent} sent`];
  if (queued) parts.push(`${queued} waiting`);
  if (level === "full") parts.push("paused: no signal");
  return parts.join(" · ");
}

// --- turning on the spot: how much of the circle is covered (row #44, 2026-10-19) ---

export interface TurnSample {
  t: number;
  heading: number | null;
  e: number | null;
  n: number | null;
  /** the position filter's 1-sigma (m), when it has one */
  sigma?: number | null;
  /** the step detector's state (arTrack MotionState): "walking" means the person is moving */
  motion?: "stationary" | "walking" | "fidget" | "nosensor" | null;
}

export interface TurnCoverage {
  deg: number;
  arcs: [number, number][];
  /** the newest sample is standing still (not walking, not moved): the ring is counting */
  still: boolean;
  /** turning while walking: the hint says "Stand still while you turn" (row #52 follow-up) */
  walkingTurn: boolean;
}

/** Turning this far while walking is "walking while turning", not a corner on the path. */
export const WALKING_TURN_DEG = 20;
/** Below this, a position change is noise whatever the filter's sigma says. */
export const STILL_FLOOR_M = 1.0;

function moved(a: TurnSample, b: TurnSample): boolean {
  if (a.e === null || a.n === null || b.e === null || b.n === null) return false;
  // GPS-aware: a move only counts when it is bigger than both positions' uncertainty allows
  const tol = Math.max(STILL_FLOOR_M, 1.5 * Math.hypot(a.sigma ?? 0, b.sigma ?? 0));
  return Math.hypot(a.e - b.e, a.n - b.n) > tol;
}

/** The degrees of the full circle the frames taken while standing HERE cover (row #44, made truthful after
 * row #52: the scan worker only stitches a true turn on the spot, so walking frames must not fill the ring).
 * Counted back from the newest sample while the person stood still: the step detector not saying "walking",
 * and no position change beyond the filter's uncertainty (at least STILL_FLOOR_M), within the last `windowS`
 * seconds; each frame covers its heading +/- hfov/2; the union, 0-360. `arcs`: the covered [from, to]
 * stretches, clockwise from north, for drawing. `walkingTurn`: the newest samples are walking while the
 * heading turned WALKING_TURN_DEG or more. */
export function turnCoverage(samples: TurnSample[], hfovDeg: number, windowS = 90): TurnCoverage {
  const last = samples[samples.length - 1];
  if (!last) return { deg: 0, arcs: [], still: false, walkingTurn: false };
  // turning while walking: the last few samples, all walking, spanning a turn
  const recent = samples.slice(-3).filter((s) => s.heading !== null && last.t - s.t <= 10);
  let turned = 0;
  for (let k = 1; k < recent.length; k++) {
    turned += Math.abs((((recent[k].heading as number) - (recent[k - 1].heading as number) + 540) % 360) - 180);
  }
  const walkingTurn = last.motion === "walking" && recent.length >= 2 && turned >= WALKING_TURN_DEG;
  const still = last.motion !== "walking";
  // the still stretch: back from the newest, until someone walked or the position moved
  const near: TurnSample[] = [];
  if (still) {
    for (let k = samples.length - 1; k >= 0; k--) {
      const s = samples[k];
      if (last.t - s.t > windowS || s.motion === "walking" || moved(s, last)) break;
      if (s.heading !== null) near.push(s);
    }
  }
  if (!near.length) return { deg: 0, arcs: [], still, walkingTurn };
  const half = Math.max(1, Math.min(179, hfovDeg / 2));
  // on a 1-degree grid: plenty for a ring on the screen
  const on = new Array<boolean>(360).fill(false);
  for (const s of near) {
    const h = (((s.heading as number) % 360) + 360) % 360;
    for (let d = Math.floor(h - half); d <= Math.ceil(h + half); d++) on[((d % 360) + 360) % 360] = true;
  }
  const deg = on.filter(Boolean).length;
  const arcs: [number, number][] = [];
  if (deg === 360) return { deg, arcs: [[0, 360]], still, walkingTurn };
  const start = on.indexOf(false);
  let from: number | null = null;
  for (let k = 1; k <= 360; k++) {
    const d = (start + k) % 360;
    if (on[d] && from === null) from = d;
    if (!on[d] && from !== null) {
      arcs.push([from, from + ((d - from + 360) % 360)]);
      from = null;
    }
  }
  return { deg, arcs, still, walkingTurn };
}

/** A phone frame's horizontal field of view from its long edge's (portrait: the narrow one). */
export function horizontalFov(longFovDeg: number, width: number, height: number): number {
  if (!(width > 0 && height > 0)) return longFovDeg;
  const r = Math.min(width, height) / Math.max(width, height);
  const narrow = (2 * Math.atan(Math.tan((longFovDeg * Math.PI) / 360) * r) * 180) / Math.PI;
  return width < height ? narrow : longFovDeg;
}
